import { useMemo, useState } from "react";
import { styled } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { buttonReset } from "../../util/styles.js";
import { mobileBreakPointCSS } from "../../defaults/theme.js";

interface ImageToRender {
  url: string;
  alt?: string;
}

interface Props {
  images: Image[];
}

export const ImageGallery: React.FC<Props> = ({ images }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const imageDistribution = useMemo<
    | {
        selected: ImageToRender;
        images: ImageToRender[];
      }
    | undefined
  >(() => {
    const selectedImage = images[selectedIndex];

    if (!selectedImage) {
      return undefined;
    }

    return {
      selected: {
        url: transformImageUrl(selectedImage, {
          width: 1060,
          height: 1060,
          fit: "cover",
        }),
        alt: selectedImage.altText,
      },
      images: images.map((r) => ({
        url: transformImageUrl(r, {
          width: 228,
          height: 228,
          fit: "cover",
        }),
        alt: r.altText,
      })),
    };
  }, [images, selectedIndex]);

  if (!imageDistribution) {
    return null;
  }

  return (
    <>
      <Primary
        src={imageDistribution.selected.url}
        alt={imageDistribution.selected.alt}
        width={530}
        height={530}
      />
      <Grid>
        {imageDistribution.images.map((i, k) => {
          if (k === selectedIndex) {
            return null;
          }

          return (
            <button
              key={k}
              onClick={() => setSelectedIndex(k)}
              aria-label={`Bytt til bilde ${i.alt || ""}`}
            >
              <img src={i.url} alt={i.alt} width={114} height={114} />
            </button>
          );
        })}
      </Grid>
    </>
  );
};

const Primary = styled.img`
  display: block;
  aspect-ratio: 1;
  width: 100%;
  height: auto;
  margin-bottom: ${(props) => props.theme.spacing.l};
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: ${(props) => props.theme.spacing.s};
  margin-bottom: ${(props) => props.theme.spacing.s};

  button {
    ${buttonReset}
    cursor: pointer;
  }

  img {
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    aspect-ratio: 1;
    width: 100%;
    height: auto;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    grid-gap: ${(props) => props.theme.spacing.m};
    margin-bottom: ${(props) => props.theme.spacing.m};
  }
`;
